/**
 * Generic OIDC/Identity Provider configuration abstraction
 * This layer provides utilities for working with OIDC configurations from the backend.
 * 
 * Note: The backend now returns OidcConfig directly (from generated API models),
 * so no mapping is needed. This file provides helper functions for URL construction.
 */

import type { OidcConfig } from '@/lib/generated-api/models/OidcConfig';

// Re-export the generated type for convenience
export type { OidcConfig };

/**
 * Constructs an OIDC authorization URL from generic configuration
 * This is provider-agnostic and can work with any OIDC-compliant provider
 * 
 * @param config - OidcConfig from backend (generated API model)
 * @param options - Additional OAuth parameters
 * @returns Authorization URL string
 */
/**
 * Generate a PKCE code verifier (random string, 43-128 characters)
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  // Base64url encode (RFC 7636)
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Generate a PKCE code challenge from a code verifier (SHA256 hash, base64url encoded)
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  // Base64url encode (RFC 7636)
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

export function constructOidcAuthorizationUrl(
  config: OidcConfig,
  options: {
    redirectUri: string;
    email?: string;
    state?: string;
    nonce?: string;
    scope?: string;
    responseType?: string;
    codeChallenge?: string; // PKCE code challenge
  }
): string {
  if (!config.issuer || !config.clientId) {
    throw new Error('OidcConfig must have issuer and clientId');
  }

  const {
    redirectUri,
    email,
    state,
    nonce,
    scope = 'openid profile email',
    responseType = 'code',
    codeChallenge
  } = options;

  // Build authorization endpoint URL
  // If authorizationEndpointPath is provided, use it; otherwise use standard OIDC path
  const authEndpoint = config.authorizationEndpointPath
    ? `${config.issuer}${config.authorizationEndpointPath}`
    : `${config.issuer}/authorize`; // Standard OIDC authorization endpoint

  const authUrl = new URL(authEndpoint);
  
  // Standard OIDC parameters
  authUrl.searchParams.set('client_id', config.clientId);
  authUrl.searchParams.set('redirect_uri', redirectUri);
  authUrl.searchParams.set('response_type', responseType);
  authUrl.searchParams.set('scope', scope);
  
  // PKCE parameters (required by Keycloak)
  if (codeChallenge) {
    authUrl.searchParams.set('code_challenge', codeChallenge);
    authUrl.searchParams.set('code_challenge_method', 'S256');
  }
  
  // Identity provider hint (provider-specific parameter name)
  if (config.idpAlias && config.idpHintParamName) {
    authUrl.searchParams.set(config.idpHintParamName, config.idpAlias);
  }
  
  // Optional parameters
  if (state) {
    authUrl.searchParams.set('state', state);
  }
  if (nonce) {
    authUrl.searchParams.set('nonce', nonce);
  }
  if (email) {
    authUrl.searchParams.set('login_hint', email);
  }

  return authUrl.toString();
}

/**
 * Provider-specific configuration presets
 * These can be extended for other identity providers
 */
export const IdentityProviderPresets = {
  keycloak: {
    idpHintParamName: 'kc_idp_hint',
    authorizationEndpointPath: '/protocol/openid-connect/auth',
    tokenEndpointPath: '/protocol/openid-connect/token'
  },
  generic: {
    idpHintParamName: 'idp_hint',
    authorizationEndpointPath: '/authorize',
    tokenEndpointPath: '/token'
  },
  // Add more providers as needed
  // auth0: { ... },
  // okta: { ... },
} as const;

